import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  FocusEvent,
  useMemo,
} from 'react';
import StationMarker from '@engineering/icons/station-marker';
import {
  SearchSuggestion,
  Runtime,
  useSearchSuggestions,
  getLocation,
} from '@silvertours/front-entities';
import { I18nLegacy } from '@silvertours/front-legacy-shared';
import { useTranslations } from 'next-intl';
// import { SearchHistory } from '@silvertours/front-features/src/SearchFormLegacy';
import { Container, StyledInput } from './SelectStation.styles';
import { useSearchContext } from '../../SearchFormLegacy/context';
import { Suggestion } from './Suggestion';
import { RentalDirection } from '../../SearchFormLegacy/context/types';

type CommonSearchInputProps = {
  direction: RentalDirection;
  id: string;
  name?: string;
  onSuggestionSelect?: (suggestion: SearchSuggestion) => void;
};

type DepartureProps = {
  direction: 'departure';
  onServiceNumbersSubmit: () => void;
} & CommonSearchInputProps;

type DestinationProps = {
  direction: 'destination';
} & CommonSearchInputProps;

type SearchInputProps = DepartureProps | DestinationProps;

const isDeparture = (props: SearchInputProps): props is DepartureProps =>
  (props as DepartureProps).direction === 'departure';

const getSubmit = (props: SearchInputProps) =>
  isDeparture(props) ? props.onServiceNumbersSubmit : undefined;

export const SelectStation = (props: SearchInputProps) => {
  const {
    getInputValue,
    isInternalCall,
    state,
    setInputValue,
    makeSetLocation,
    setError,
  } = useSearchContext();

  const { direction, id, name, onSuggestionSelect } = props;
  const handleSubmit = getSubmit(props);
  const setLocation = makeSetLocation(direction);
  const initValue = getInputValue(direction);
  const [value, setValue] = useState(initValue);
  const shouldSearchServiceNumbers =
    isInternalCall && direction === 'departure' && value.startsWith('#');

  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [showLocationSuggest, setShowLocationSuggest] = useState(false);
  const [activeDescendant, setActiveDescendant] = useState<string | undefined>(
    undefined,
  );
  const fieldRef = useRef<HTMLInputElement>(null);
  const t = useTranslations('features.stage');
  const { language, market } = I18nLegacy.useLocale();
  const { consumerApiSettings } = Runtime.useConsumerApi();

  const { clearResults, fetchSuggestions, loading, results } =
    useSearchSuggestions(language, market, consumerApiSettings);

  useLayoutEffect(() => {
    const field = fieldRef.current;
    const focusField = () => {
      field?.focus();
      field?.select();
    };

    focusField();

    // On coarse-pointer (touch) devices, headlessui's Dialog FocusTrap
    // unconditionally moves focus to its own container in a passive effect
    // that always runs after this one, undoing the focus above. Reclaim it
    // the moment that steal happens.
    const dialog = field?.closest<HTMLElement>('[role="dialog"]');
    dialog?.addEventListener('focus', focusField, { once: true });

    return () => dialog?.removeEventListener('focus', focusField);
  }, []);

  const isExpanded = useMemo(
    () => showLocationSuggest || showSearchHistory,
    [showLocationSuggest, showSearchHistory],
  );

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setError({ kind: 'locationError', message: '' });

    if (event.target.value === '') {
      setShowSearchHistory(true);
    } else {
      fetchSuggestions(event.target.value);
      setShowLocationSuggest(true);
    }
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (!event.relatedTarget?.className.includes('searchHistory')) {
      setShowSearchHistory(false);
    }

    if (!(
      event.relatedTarget?.className.includes('inputCloseIcon') ||
      event.relatedTarget?.id.includes('locationSuggest')
    )) {
      setShowLocationSuggest(false);
    }
  };

  const handleChange = (inputValue: string) => {
    setValue(inputValue);

    if (inputValue === '') {
      clearResults();
      setShowSearchHistory(true);
      setShowLocationSuggest(false);
    } else {
      setShowSearchHistory(false);
      if (shouldSearchServiceNumbers) {
        setShowLocationSuggest(false);
      } else {
        setShowLocationSuggest(true);
        fetchSuggestions(inputValue);
      }
    }
  };

  useEffect(() => {
    if (initValue !== '' && value === initValue) {
      setShowLocationSuggest(true);
      fetchSuggestions(initValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initValue]);

  const handleSuggestionValue = (suggestion?: SearchSuggestion) => {
    if (suggestion) {
      const location = getLocation(suggestion);
      setShowLocationSuggest(false);
      setInputValue(direction, location.name);
      setLocation(location);
      clearResults();
      onSuggestionSelect?.(suggestion);
    } else {
      handleChange(value);
      if (shouldSearchServiceNumbers && !!handleSubmit) {
        handleSubmit();
      }
    }
  };

  if (!state) {
    return null;
  }

  const error =
    (!loading &&
      !results?.length &&
      !!value &&
      t(`search.errors.${direction}`)) ||
    state.locationError ||
    undefined;

  return (
    // Clarity unmask (LPOT-1716): mobile location search input + autosuggest results.
    <Container data-clarity-unmask="true">
      <StyledInput
        label={t(`search.selectDestination.${direction}.label`)}
        ref={fieldRef}
        value={value}
        placeholder={t(`search.selectDestination.${direction}.placeholder`)}
        icon={<StationMarker />}
        iconPosition="left"
        autoComplete="off"
        forcedLabel
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={event => handleChange(event.target.value)}
        error={error}
        name={name ?? id}
        id={id}
        cleanable
        autoFocus // eslint-disable-line jsx-a11y/no-autofocus
        role="combobox"
        aria-controls="locationSuggest"
        aria-autocomplete="list"
        aria-haspopup="grid"
        aria-expanded={isExpanded ? 'true' : 'false'}
        aria-activedescendant={isExpanded ? activeDescendant : undefined}
      />
      {/* showSearchHistory && ( */}
      {/*   <SearchHistory */}
      {/*     onItemClick={clearResults} */}
      {/*     onLastElementBlur={() => { */}
      {/*       setShowSearchHistory(false); */}
      {/*     }} */}
      {/*   /> */}
      {/* ) */}
      {showLocationSuggest && (
        <Suggestion
          connectedInputContainer={fieldRef}
          onLastElementBlur={() => {
            setShowLocationSuggest(false);
          }}
          onItemClick={handleSuggestionValue}
          onItemSelect={setActiveDescendant}
          results={results}
          direction={direction}
        />
      )}
    </Container>
  );
};
